import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Event } from '../types/event';
import type { Player } from '../types/player';

interface GlobalModalState {
  eventForm: { isOpen: boolean; event: Event | null; selectedDate: Date | null };
  playerForm: { isOpen: boolean; player: Player | null };
}

interface GlobalModalContextType {
  modals: GlobalModalState;
  openEventForm: (event?: Event | null, selectedDate?: Date | null) => void;
  openPlayerForm: (player?: Player | null) => void;
  closeEventForm: () => void;
  closePlayerForm: () => void;
  onEventSaved: (() => void) | null;
  onPlayerSaved: (() => void) | null;
  setOnEventSaved: (cb: (() => void) | null) => void;
  setOnPlayerSaved: (cb: (() => void) | null) => void;
}

const GlobalModalContext = createContext<GlobalModalContextType>({
  modals: {
    eventForm: { isOpen: false, event: null, selectedDate: null },
    playerForm: { isOpen: false, player: null },
  },
  openEventForm: () => {},
  openPlayerForm: () => {},
  closeEventForm: () => {},
  closePlayerForm: () => {},
  onEventSaved: null,
  onPlayerSaved: null,
  setOnEventSaved: () => {},
  setOnPlayerSaved: () => {},
});

export const useGlobalModal = () => useContext(GlobalModalContext);

export const GlobalModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<GlobalModalState>({
    eventForm: { isOpen: false, event: null, selectedDate: null },
    playerForm: { isOpen: false, player: null },
  });

  const [onEventSaved, setOnEventSavedState] = useState<(() => void) | null>(null);
  const [onPlayerSaved, setOnPlayerSavedState] = useState<(() => void) | null>(null);

  const openEventForm = useCallback((event?: Event | null, selectedDate?: Date | null) => {
    setModals(prev => ({
      ...prev,
      eventForm: { isOpen: true, event: event ?? null, selectedDate: selectedDate ?? null },
    }));
  }, []);

  const openPlayerForm = useCallback((player?: Player | null) => {
    setModals(prev => ({
      ...prev,
      playerForm: { isOpen: true, player: player ?? null },
    }));
  }, []);

  const closeEventForm = useCallback(() => {
    setModals(prev => ({
      ...prev,
      eventForm: { isOpen: false, event: null, selectedDate: null },
    }));
  }, []);

  const closePlayerForm = useCallback(() => {
    setModals(prev => ({
      ...prev,
      playerForm: { isOpen: false, player: null },
    }));
  }, []);

  const setOnEventSaved = useCallback((cb: (() => void) | null) => {
    setOnEventSavedState(() => cb);
  }, []);

  const setOnPlayerSaved = useCallback((cb: (() => void) | null) => {
    setOnPlayerSavedState(() => cb);
  }, []);

  return (
    <GlobalModalContext.Provider value={{
      modals,
      openEventForm, openPlayerForm,
      closeEventForm, closePlayerForm,
      onEventSaved, onPlayerSaved,
      setOnEventSaved, setOnPlayerSaved,
    }}>
      {children}
    </GlobalModalContext.Provider>
  );
};
